import { FC, useCallback, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { BrochureBasket, getBrochures } from '../../api/brochures';
import ButtonComponent from '../../components/ButtonComponent/ButtonComponent';
import LoadingModal from '../../components/LoadingModal/LoadingModal';
import MessageToastComponent from '../../components/MessageToastComponent/MessageToastComponent';
import { getRequestedLanguages } from '../../locale/languages';
import { format } from 'date-fns';

const PER_PAGE = 10;

const BrochuresPage: FC = () => {
    const language = getRequestedLanguages(window.errea_accountUserData.language);
    const [collectionSearch, setCollectionSearch] = useState('');
    const [companySearch, setCompanySearch] = useState('');
    const [brochures, setBrochures] = useState<BrochureBasket[]>([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const fetchBrochures = useCallback(
        async (pageNum: number, name: string, company: string, append = false) => {
            try {
                setLoading(true);
                const result = await getBrochures(pageNum, PER_PAGE, name || undefined, company || undefined);
                const items = result.data;
                if (append) {
                    setBrochures((prev) => [...prev, ...items]);
                } else {
                    setBrochures(items);
                }
                setHasMore(result.links.next !== null);
            } catch {
                setError(language.brochures.list.error_fetching ?? 'Errore nel caricamento delle brochure');
            } finally {
                setLoading(false);
            }
        },
        [language],
    );

    useEffect(() => {
        fetchBrochures(1, '', '');
    }, []);

    const handleSearch = useCallback(() => {
        setPage(1);
        fetchBrochures(1, collectionSearch, companySearch);
    }, [collectionSearch, companySearch, fetchBrochures]);

    const handleLoadMore = useCallback(() => {
        const nextPage = page + 1;
        setPage(nextPage);
        fetchBrochures(nextPage, collectionSearch, companySearch, true);
    }, [page, collectionSearch, companySearch, fetchBrochures]);

    const handleHideMessage = useCallback(() => {
        if (error) setError('');
    }, [error]);

    return (
        <div className="brochuresPage">
            <div className="brochuresFilters">
                <div className="brochuresSearchInput">
                    <label>{language.brochures.list.collection_label}</label>
                    <div className="searchInputWrapper">
                        <input
                            type="text"
                            value={collectionSearch}
                            onChange={(e) => setCollectionSearch(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                            placeholder={language.brochures.list.collection_placeholder}
                        />
                        <button className="searchIconBtn" onClick={handleSearch}>
                            <i className="icon errea-lente"></i>
                        </button>
                    </div>
                </div>
                <div className="brochuresSearchInput">
                    <label>{language.brochures.list.company_label}</label>
                    <div className="searchInputWrapper">
                        <input
                            type="text"
                            value={companySearch}
                            onChange={(e) => setCompanySearch(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                            placeholder={language.brochures.list.company_placeholder}
                        />
                        <button className="searchIconBtn" onClick={handleSearch}>
                            <i className="icon errea-lente"></i>
                        </button>
                    </div>
                </div>
            </div>

            <div className="tableAccountContainer">
                <table className="tableAccount">
                    <thead>
                        <tr>
                            <th className="tableHeading custom">{language.brochures.list.table_name}</th>
                            <th className="tableHeading custom">{language.brochures.list.table_company}</th>
                            <th className="tableHeading custom">{language.brochures.list.table_products_count}</th>
                            <th className="tableHeading custom">{language.brochures.list.table_created_at}</th>
                            <th className="tableHeading custom"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {brochures.map((item) => (
                            <tr key={item.id} className="tableRow">
                                <td>{item.name}</td>
                                <td>{item.company}</td>
                                <td>{item.products_count}</td>
                                <td>{format(new Date(item.created_at), language.general.date_format)}</td>
                                <td>
                                    <ButtonComponent
                                        style="table"
                                        text={language.brochures.list.details}
                                        icon_right="arrow-right"
                                        onClick={() => {
                                            window.location.href = `${window?.Shopify?.routes?.root ?? '/'}pages/brochure-details?id=${item.id}`;
                                        }}
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {hasMore && (
                <div className="brochuresLoadMore">
                    <ButtonComponent
                        style="outline"
                        text={language.brochures.list.load_more}
                        onClick={handleLoadMore}
                    />
                </div>
            )}

            <LoadingModal isVisible={loading} />
            <MessageToastComponent message={error} type={'error'} onHide={handleHideMessage} />
        </div>
    );
};

const BrochuresPageId = document.getElementById('BrochuresPage');
const root = BrochuresPageId && createRoot(BrochuresPageId as HTMLElement);
root && root.render(<BrochuresPage />);
